import { Grid } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { VerticalDivider } from "components/alove/VerticalDivider";
import {
  HeaderIndication,
  HeaderIndicationProps,
} from "layouts/itemPageContainer/components/headerIndication";
import { ResponseType } from "models/batch/Question";
import { GeneralCode, GeneralCodeType } from "models/content/GeneralCode";
import { IntroductionStatusReason } from "models/introduction/Introduction";
import { ProfileQResponse } from "models/profile/ProfileResponse";

import { useEffect, useState } from "react";
import { getProfilePartnersResponses, getProfileResponses } from "utils/enduserUtils";
import { getGeneralCodes } from "utils/generalCodesUtils";
import { ellipsis, groupBy, groupByArray } from "utils/globalsUtils";
import { getEnded } from "utils/introductionUtils";
import { AppFeature, getSetting, hasFeature, SettingName } from "utils/settingsUtils";

const MDTypography = require("components/MDTypography").default;

type EndedIntro = {
  introductionId: string;
  reason: IntroductionStatusReason;
  isMe: boolean;
};

export const ProfileDeclines = ({ profileId }) => {
  const [stats, setStats] = useState({} as { [key: string]: ProfileQResponse[] });
  const [partnerStats, setPartnerStats] = useState({} as { [key: string]: ProfileQResponse[] });
  const [other, setOther] = useState([] as ProfileQResponse[]);
  const [partnerOther, setPartnerOther] = useState([] as ProfileQResponse[]);
  const [endedReasons, setEndedReasons] = useState([] as EndedIntro[]);
    const [unmatchReasons, setUnmatchReasons] = useState([] as GeneralCode[]);
  
  useEffect(() => {
    const qIds = getSetting(SettingName.DeclineReasonQuestions)?.split(",");
    if (!profileId || !qIds) return;
    getProfileResponses(profileId, qIds).then((r) => fill(r, setStats, setOther));

    getProfilePartnersResponses(profileId, qIds).then((r) =>
      fill(r, setPartnerStats, setPartnerOther)
    );

    getEnded(profileId).then((r) => setEndedReasons(r));

     getGeneralCodes(GeneralCodeType.UnmatchReason).then((res) => {
            setUnmatchReasons(res);
          });
  }, [profileId]);

  const fill = (responses: ProfileQResponse[], _setStats, _setOther) => {
    _setStats(
      groupByArray(
        responses.filter((r) => r.responseType != ResponseType.Text),
        (r) => r.response.map((r) => ellipsis(r, 15))
      )
    );
    _setOther(responses.filter((r) => r.responseType == ResponseType.Text));
  };

  const openIntros = (responses: ProfileQResponse[]) => {
    responses
      .filter((r) => r.introductionId)
      .forEach((r) => {
        window.open(`#/introductions/${r.introductionId}`);
      });
  };

  const buildList = (
    title,
    _stats: { [key: string]: ProfileQResponse[] },
    _others: ProfileQResponse[]
  ) => (
    <>
      <MDTypography variant="body1" color="secondary">
        {title}
      </MDTypography>
      <Grid container display="flex" justifyContent="start" gap={1} flexDirection="row">
        {Object.entries(_stats)
          .map(([key, value], index) => [
            <HeaderIndication
              title={key}
              value={value.length.toString() as string}
              onClick={() => openIntros(value)}
            />,
            index != Object.entries(_stats).length - 1 && (
              <VerticalDivider height="40px" key={index} />
            ),
          ])
          .flat()}
      </Grid>
      {!!_others?.length && (
        <Grid container display="flex" flexDirection="column" mt={2}>
          <MDTypography variant="body2" color="secondary">
            Other Reasons:
          </MDTypography>
          {_others.map((o, index) => (
            <a href={`#/introductions/${o.introductionId}`}>
              <MDTypography key={index} variant="body2">
                💬 {o.response}
              </MDTypography>
            </a>
          ))}
        </Grid>
      )}
    </>
  );

  return (
    <Grid container display="flex" flexDirection="row" gap={2}>
      <Grid size="grow">
        {buildList("My Declines", stats, other)}
      </Grid>
      <VerticalDivider height="140px" />
      <Grid size="grow">
        {buildList("Partners Declines", partnerStats, partnerOther)}
      </Grid>
      {hasFeature(AppFeature.matchmaker) && (
        <Grid size={12}>
          <MDTypography variant="body1" color="secondary">
            Ended Shiduchim:
          </MDTypography>
          <Grid container display="flex" flexDirection="column" mt={2}>
            <DataGridPro
              autoHeight
              rows={endedReasons}
              density="compact"

              onRowClick={(e) => open("#/introductions/" + e.row.introductionId)}
              columns={[
                {
                  field: "introductionId",
                  headerName: "Id",
                  flex: 1
                },

                {
                  field: "reason",
                  headerName: "Reason",
                  valueGetter: (v,row:EndedIntro) => unmatchReasons.find((r) => r.id.toString() == row.reason.unmatchReason)?.name,
                  
                },
                {
                  field: "otherReason",
                  headerName: "Other Reason",
                  valueGetter: (v,row:EndedIntro) => row.reason.unmatchReasonOther,
                  flex: 1,
                },
                {
                  field: "me",
                  headerName: "Me",
                  valueGetter: (v,row:EndedIntro) =>
                    row.isMe ? row.reason.unmatchReasonInitiator : row.reason.unmatchReasonResponder,
                  flex: 1,
                  
                },
                {
                  field: "partner",
                  headerName: "Partner",
                  valueGetter: (v,row:EndedIntro) =>
                    row.isMe ? row.reason.unmatchReasonResponder : row.reason.unmatchReasonInitiator,
                  flex: 1
                },
              ]}
              getRowId={(row) => row.introductionId}
              hideFooter
              />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
